import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storageKey: 'academy-auth',
  },
});

// [중요] 예전에는 "Refresh Token" 관련 에러가 한 번이라도 뜨면 곧바로
// signOut()으로 로컬 세션을 지워버렸어. 그런데 Supabase는 백그라운드에서
// 토큰을 주기적으로 자동 갱신하는데, 이때 네트워크가 잠깐 끊기거나 일시적
// 오류가 나도 같은 메시지가 뜰 수 있어. 그 결과 멀쩡한 로그인 세션이 조용히
// 로그아웃되고, 이후 모든 쿼리가 익명으로 실행돼서 RLS가 데이터를 전부
// 막아버렸어 (= 학생명단이 사라진 것처럼 보임).
//
// 그래서 이제 전역 핸들러는 "콘솔에 지저분한 에러가 뜨는 것만" 조용히
// 막아주고, 절대 세션을 강제로 지우지 않아. 세션 정리는 실제로 세션이
// 확인되지 않을 때(getSafeSession)만 신중하게 처리해.
if (typeof window !== 'undefined') {
  // 두 종류의 에러를 명확히 구분해.
  //
  // 1) "Refresh Token Not Found" / "Invalid Refresh Token"
  //    → 서버가 "이 refresh token은 이미 없어요(만료·폐기됨)"라고 명확히 답한
  //      진짜 세션 만료 신호. 이건 일시적 네트워크 문제와 달리 다시 시도해도
  //      절대 살아나지 않아. 이 경우엔 로컬 세션을 정리하고 로그인 화면으로
  //      보내서 새로 로그인(새 토큰 발급)하도록 유도해야 해.
  //
  // 2) "Failed to fetch" / "Network request failed" / "fetch failed" 등
  //    → 네트워크가 잠깐 끊긴 일시적 오류. 이건 세션을 지우면 안 되고,
  //      그냥 조용히 넘겨야 해 (다음 자동 갱신 때 다시 시도).
  const isRefreshTokenDead = (message: string) =>
    message.includes('Invalid Refresh Token') ||
    message.includes('Refresh Token Not Found');

  const isTransientNetworkError = (message: string) =>
    message.includes('Failed to fetch') ||
    message.includes('Network request failed') ||
    message.includes('fetch failed') ||
    message.includes('Load failed') ||
    message.includes('network error');

  // 중복 로그아웃 방지용 플래그
  let handledRefreshTokenDeath = false;

  const handleRejection = (event: PromiseRejectionEvent) => {
    const reason = event?.reason;
    const message =
      typeof reason === 'string'
        ? reason
        : (reason as { message?: string } | null)?.message || '';

    if (isRefreshTokenDead(message)) {
      event.preventDefault();
      event.stopImmediatePropagation();

      if (handledRefreshTokenDeath) return;
      handledRefreshTokenDeath = true;

      // 진짜 세션 만료 → 로컬 세션만 정리 (서버에 로그아웃 요청은
      // 어차피 토큰이 없어서 실패할 테니 scope: 'local'로만).
      // onAuthStateChange가 SIGNED_OUT을 감지해서 로그인 화면으로 보내줘.
      supabase.auth.signOut({ scope: 'local' }).catch(() => {});
      return;
    }

    if (isTransientNetworkError(message)) {
      // 일시적 네트워크 오류 → 콘솔 노이즈만 억제하고 세션은 유지.
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };

  window.addEventListener('unhandledrejection', handleRejection);

  window.addEventListener('error', (event) => {
    const msg = event?.message || '';
    if (isRefreshTokenDead(msg)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!handledRefreshTokenDeath) {
        handledRefreshTokenDeath = true;
        supabase.auth.signOut({ scope: 'local' }).catch(() => {});
      }
    } else if (isTransientNetworkError(msg)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  });
}

// getSafeSession()은 현재 세션을 안전하게 가져와.
// 1) 우선 getUser()로 서버에 토큰이 실제로 유효한지 검증 (로컬 캐시가 아니라 서버 확인)
// 2) 토큰이 만료됐으면 refreshSession()으로 갱신 시도
// 3) 갱신까지 실패하면 그때만 로컬 세션을 정리하고 null 반환
export async function getSafeSession() {
  try {
    // 서버 검증: getUser()는 로컬에 저장된 토큰이 아니라
    // 실제 서버에 물어봐서 "지금도 유효한 로그인인지" 확인해.
    // 로컬 세션은 남아있어도 토큰이 만료됐으면 여기서 걸러져.
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (!userError && userData.user) {
      const { data } = await supabase.auth.getSession();
      return data.session;
    }

    // 서버 검증 실패 → 토큰 갱신을 한 번 더 시도
    const { data: refreshed, error: refreshError } =
      await supabase.auth.refreshSession();
    if (!refreshError && refreshed.session) {
      return refreshed.session;
    }

    // 여기까지 왔으면 진짜로 유효한 세션이 없는 상태야. 이제 정리해.
    await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
    return null;
  } catch {
    // 예상치 못한 예외에서도 곧바로 로그아웃시키지 않고, 마지막으로
    // 갱신을 한 번 더 시도해봐.
    try {
      const { data: refreshed } = await supabase.auth.refreshSession();
      if (refreshed?.session) return refreshed.session;
    } catch {
      /* noop */
    }
    return null;
  }
}

export type ProfileStatus = 'pending' | 'approved' | 'rejected';

export type ProfileRole = 'operator' | 'owner' | 'teacher';

export type Profile = {
  id: string;
  full_name: string | null;
  role: ProfileRole;
  phone: string | null;
  status: ProfileStatus;
  academy_id: string | null;
  academy_name: string | null;
  address: string | null;
  created_at: string;
};

export type Academy = {
  id: string;
  name: string;
  address: string | null;
  owner_id: string | null;
  created_at: string;
};